import React, { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Toast } from 'primereact/toast';
import BillPreview from './BillPreview';
import axios from 'axios';

// PrimeReact imports
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { InputNumber } from 'primereact/inputnumber';
import { Badge } from 'primereact/badge';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const API_BASE_URL = process.env.REACT_APP_API_PATH || 'https://localhost:7000/api';

const AddItemsToBooking = ({ 
  bookingReference, 
  bookingDetails: initialBookingDetails,
  onItemsAdded,
  onBackToSearch,
  onCompleteOrder 
}) => {
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(initialBookingDetails || null);
  const [billCalculation, setBillCalculation] = useState(null);
  const [showBill, setShowBill] = useState(false);
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [itemTypes, setItemTypes] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [addedItemsCount, setAddedItemsCount] = useState(0);
  const toast = useRef(null);
  
  const { control, handleSubmit, watch, setValue, getValues, reset } = useForm({
    defaultValues: {
      items: [
        {
          itemType: '',
          itemCategory: '',
          quantity: 1,
          unitWeight: 0,
          unitPrice: 0,
          treatment: '',
          instructions: '',
        }
      ]
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "items"
  });

  const watchedItems = watch("items");

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (bookingReference && !initialBookingDetails) {
      fetchBookingDetails();
    } else if (initialBookingDetails) {
      setBooking(initialBookingDetails);
      // If there are existing items, calculate bill
      if (initialBookingDetails.orderItems && initialBookingDetails.orderItems.length > 0) {
        calculateBill();
      }
    }
  }, [bookingReference]);

  useEffect(() => {
    if (watchedItems && watchedItems.length > 0 && watchedItems.some(item => item.unitPrice > 0)) {
      const timeoutId = setTimeout(() => {
        calculateBill();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [watchedItems]);

  // Load master data
  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setLoadingMasterData(true);
    await Promise.all([loadItemTypes(), loadTreatments()]);
    setLoadingMasterData(false);
  };

  const loadItemTypes = async () => {
  try {
    // Get partnerShopId from booking data if available
    const partnerShopId = booking?.partnerid || booking?.partnerid || null;
    
    const url = partnerShopId 
      ? `${API_BASE_URL}customer/itemtypes?partnerShopId=${partnerShopId}`
      : `${API_BASE_URL}customer/itemtypes`;
    
    const response = await axios.get(url, {});
    const items = response.data.records || response.data || [];
    setItemTypes(items);
    
    // Set default values for first item if available and no item type selected
    if (items.length > 0 && fields.length > 0 && !getValues(`items.0.itemType`)) {
      const firstItem = items[0];
      const defaultTreatment = treatments.length > 0 ? (treatments[0].Treatement || treatments[0].treatement) : '';
      
      setValue(`items.0.itemType`, firstItem.Itemtype || firstItem.itemtype);
      setValue(`items.0.itemCategory`, firstItem.Category || firstItem.category || '');
      setValue(`items.0.unitPrice`, firstItem.BasePrice || firstItem.baseprice || 0);
      setValue(`items.0.treatment`, defaultTreatment);
    }
  } catch (error) {
    console.error('Error loading item types:', error);
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load item types',
      life: 3000
    });
    // Fallback data
    setItemTypes([
      { Itemtype: 'Shirts', BasePrice: 10.00, Category: 'Clothing', MaxQuantityPerBooking: 20 },
      { Itemtype: 'Pants', BasePrice: 12.00, Category: 'Clothing', MaxQuantityPerBooking: 15 },
      { Itemtype: 'Bed Sheets', BasePrice: 25.00, Category: 'Bedding', MaxQuantityPerBooking: 10 }
    ]);
  }
};


  const loadTreatments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}customer/treatments`, {
        timeout: 10000
      });
      const treatmentsData = response.data.records || response.data || [];
      setTreatments(treatmentsData);
    } catch (error) {
      console.error('Error loading treatments:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load treatments',
        life: 3000
      });
      // Fallback data
      setTreatments([
        { Treatement: 'Standard Wash', PriceMultiplier: 1.0, Description: 'Regular washing' },
        { Treatement: 'Delicate Care', PriceMultiplier: 1.5, Description: 'Gentle cycle for delicate fabrics' },
        { Treatement: 'Stain Removal', PriceMultiplier: 2.0, Description: 'Special stain treatment' }
      ]);
    }
  };

  const handleItemTypeChange = (index, selectedItem) => {
    if (!selectedItem) {
      setValue(`items.${index}.itemType`, '');
      setValue(`items.${index}.itemCategory`, '');
      setValue(`items.${index}.unitPrice`, 0);
      return;
    }
    
    setValue(`items.${index}.itemType`, selectedItem.Itemtype || selectedItem.itemtype);
    setValue(`items.${index}.itemCategory`, selectedItem.Category || selectedItem.category || '');
    setValue(`items.${index}.unitPrice`, selectedItem.BasePrice || selectedItem.baseprice || 0);
    
    // Trigger recalculation
    calculateBill();
  };

  const handleTreatmentChange = (index, selectedTreatment) => {
    if (!selectedTreatment) {
      setValue(`items.${index}.treatment`, '');
      return;
    }
    setValue(`items.${index}.treatment`, selectedTreatment.Treatement || selectedTreatment.treatement);
  };

  const fetchBookingDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}BookingItems/booking/${bookingReference}`);
      if (response.data.success) {
        setBooking(response.data.data);
        // If there are existing items, show bill preview instead of add form
        if (response.data.data.orderItems && response.data.data.orderItems.length > 0) {
          // Don't show bill automatically, just show existing items info
          await calculateBill();
        }
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to fetch booking details',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateBill = async () => {
    if (!bookingReference) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}BookingItems/calculate-bill/${bookingReference}`);
      if (response.data.success) {
        setBillCalculation(response.data.data);
      }
    } catch (error) {
      console.error('Error calculating bill:', error);
    }
  };

  const validateItems = (items) => {
    const newErrors = {};
    items.forEach((item, index) => {
      if (!item.itemType) {
        newErrors[`itemType_${index}`] = 'Item type is required';
      }
      if (!item.quantity || item.quantity < 1) {
        newErrors[`quantity_${index}`] = 'Quantity must be at least 1';
      }
      if (!item.unitPrice || item.unitPrice <= 0) {
        newErrors[`unitPrice_${index}`] = 'Valid unit price is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (data) => {
    const validItems = data.items.filter(item => item.itemType && item.quantity > 0 && item.unitPrice > 0);
    
    if (validItems.length === 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please add at least one valid item',
        life: 3000
      });
      return;
    }

    if (!validateItems(validItems)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix validation errors',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}BookingItems/add-items`, {
        bookingReference: bookingReference,
        items: validItems.map(item => ({
          itemType: item.itemType,
          itemCategory: item.itemCategory,
          quantity: item.quantity,
          unitWeight: item.unitWeight || 0,
          unitPrice: item.unitPrice,
          treatment: item.treatment,
          instructions: item.instructions || '',
        })),
      });
      
      if (response.data.success) {
        setAddedItemsCount(validItems.length);
        
        // Refresh booking data
        await fetchBookingDetails();
        await calculateBill();
        
        // Reset form for new items
        reset({
          items: [
            {
              itemType: '',
              itemCategory: '',
              quantity: 1,
              unitWeight: 0,
              unitPrice: 0,
              treatment: '',
              instructions: '',
            }
          ]
        });
        
        // Call onItemsAdded callback
        if (onItemsAdded) {
          onItemsAdded(response.data.data);
        }
        
        // Show success dialog
        setShowSuccessDialog(true);
        
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `${validItems.length} item(s) added successfully!`,
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error adding items:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to add items',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateItemTotal = (index) => {
    const item = watchedItems[index];
    if (item && item.quantity && item.unitPrice) {
      return (item.quantity * item.unitPrice).toFixed(2);
    }
    return '0.00';
  };

  const calculateTotal = () => {
    let total = 0;
    watchedItems.forEach(item => {
      if (item.quantity && item.unitPrice) {
        total += item.quantity * item.unitPrice;
      }
    });
    return total.toFixed(2);
  };

  // Get dropdown options
  const itemTypeOptions = itemTypes.map(item => ({
    label: `${item.Itemtype || item.itemtype} - $${(item.BasePrice || item.baseprice || 0).toFixed(2)}`,
    value: item,
    original: item
  }));

  const treatmentOptions = treatments.map(treatment => ({
    label: treatment.Treatement || treatment.treatement,
    value: treatment,
    original: treatment
  }));

  // Get selected item type for a row
  const getSelectedItemType = (itemType) => {
    return itemTypes.find(it => (it.Itemtype || it.itemtype) === itemType);
  };

  // Get selected treatment for a row
  const getSelectedTreatment = (treatment) => {
    return treatments.find(t => (t.Treatement || t.treatement) === treatment);
  };

  const handleAddMoreItems = () => {
    setShowSuccessDialog(false);
    // Clear any validation errors
    setErrors({});
    // Focus on first item
    setTimeout(() => {
      const firstDropdown = document.querySelector('.p-dropdown');
      if (firstDropdown) firstDropdown.focus();
    }, 100);
  };

  const handleViewBill = () => {
    setShowSuccessDialog(false);
    setShowBill(true);
  };

  const handleCompleteAndExit = () => {
    setShowSuccessDialog(false);
    if (onCompleteOrder) {
      onCompleteOrder();
    }
  };

  const successDialogFooter = (
    <div className="flex justify-content-center gap-2 flex-wrap">
      <Button 
        label="Add More Items" 
        icon="pi pi-plus" 
        onClick={handleAddMoreItems} 
        className="p-button-outlined"
      />
      <Button 
        label="View Bill" 
        icon="pi pi-receipt" 
        onClick={handleViewBill} 
        className="p-button-primary"
      />
      <Button 
        label="Complete Order" 
        icon="pi pi-check" 
        onClick={handleCompleteAndExit} 
        className="p-button-success"
      />
    </div>
  );

  // Mobile card view for items
  const renderMobileItemCard = (item, index) => {
    const selectedItemType = getSelectedItemType(item.itemType);
    const selectedTreatment = getSelectedTreatment(item.treatment);
    const maxQuantity = selectedItemType?.MaxQuantityPerBooking || 100;

    return (
      <Card key={index} className="mb-3" style={{ borderRadius: '12px', marginBottom: '16px' }}>
        <div className="flex justify-content-between align-items-start mb-3">
          <Badge value={`Item ${index + 1}`} severity="info" />
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-text"
            onClick={() => remove(index)}
            disabled={fields.length === 1}
          />
        </div>
        
        <div className="field mb-3">
          <label className="font-semibold block mb-2">Item Type *</label>
          <Dropdown
            value={selectedItemType}
            options={itemTypeOptions}
            onChange={(e) => handleItemTypeChange(index, e.value)}
            optionLabel="label"
            placeholder="Select item type"
            className={`w-full ${errors[`itemType_${index}`] ? 'p-invalid' : ''}`}
            filter
            showClear
            loading={loadingMasterData}
          />
          {errors[`itemType_${index}`] && (
            <small className="text-red-500">{errors[`itemType_${index}`]}</small>
          )}
        </div>
        
        <div className="field mb-3">
          <label className="font-semibold block mb-2">Category</label>
          <InputText
            value={item.itemCategory}
            disabled
            className="w-full bg-gray-100"
          />
        </div>
        
        <div className="grid mb-3">
          <div className="col-6 pr-2">
            <label className="font-semibold block mb-2">Quantity *</label>
            <InputNumber
              value={item.quantity}
              onValueChange={(e) => {
                const newItems = [...watchedItems];
                newItems[index].quantity = e.value;
                setValue('items', newItems);
              }}
              min={1}
              max={maxQuantity}
              showButtons
              buttonLayout="horizontal"
              step={1}
              className={`w-full ${errors[`quantity_${index}`] ? 'p-invalid' : ''}`}
            />
            {maxQuantity < 100 && (
              <small className="text-gray-500">Max: {maxQuantity}</small>
            )}
            {errors[`quantity_${index}`] && (
              <small className="text-red-500">{errors[`quantity_${index}`]}</small>
            )}
          </div>
          
          <div className="col-6 pl-2">
            <label className="font-semibold block mb-2">Unit Weight (kg)</label>
            <InputNumber
              value={item.unitWeight}
              onValueChange={(e) => {
                const newItems = [...watchedItems];
                newItems[index].unitWeight = e.value;
                setValue('items', newItems);
              }}
              min={0}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={2}
              className="w-full"
              placeholder="Weight"
            />
          </div>
        </div>
        
        <div className="grid mb-3">
          <div className="col-6 pr-2">
            <label className="font-semibold block mb-2">Unit Price *</label>
            <InputNumber
              value={item.unitPrice}
              onValueChange={(e) => {
                const newItems = [...watchedItems];
                newItems[index].unitPrice = e.value;
                setValue('items', newItems);
              }}
              min={0}
              mode="currency"
              currency="USD"
              locale="en-US"
              className={`w-full ${errors[`unitPrice_${index}`] ? 'p-invalid' : ''}`}
            />
            {errors[`unitPrice_${index}`] && (
              <small className="text-red-500">{errors[`unitPrice_${index}`]}</small>
            )}
          </div>
          
          <div className="col-6 pl-2">
            <label className="font-semibold block mb-2">Total</label>
            <div className="text-xl font-bold text-primary mt-2">
              ${calculateItemTotal(index)}
            </div>
          </div>
        </div>
        
        <div className="field mb-3">
          <label className="font-semibold block mb-2">Treatment</label>
          <Dropdown
            value={selectedTreatment}
            options={treatmentOptions}
            onChange={(e) => handleTreatmentChange(index, e.value)}
            optionLabel="label"
            placeholder="Select treatment"
            className="w-full"
            clearable
            loading={loadingMasterData}
          />
          {selectedTreatment?.Description && (
            <small className="text-gray-500">{selectedTreatment.Description}</small>
          )}
        </div>
        
        <div className="field mb-2">
          <label className="font-semibold block mb-2">Instructions</label>
          <InputText
            value={item.instructions}
            onChange={(e) => {
              const newItems = [...watchedItems];
              newItems[index].instructions = e.target.value;
              setValue('items', newItems);
            }}
            className="w-full"
            placeholder="Special instructions"
          />
        </div>
      </Card>
    );
  };

  // DataTable column templates for desktop
  const itemTypeTemplate = (rowData, { rowIndex }) => {
    const selectedItemType = getSelectedItemType(rowData.itemType);

    return (
      <Dropdown
        value={selectedItemType}
        options={itemTypeOptions}
        onChange={(e) => handleItemTypeChange(rowIndex, e.value)}
        optionLabel="label"
        placeholder="Select item type"
        className={`w-full ${errors[`itemType_${rowIndex}`] ? 'p-invalid' : ''}`}
        filter
        showClear
        loading={loadingMasterData}
      />
    );
  };

  const categoryTemplate = (rowData) => {
    return (
      <span className="text-gray-600">{rowData.itemCategory || '-'}</span>
    );
  };

  const quantityTemplate = (rowData, { rowIndex }) => {
    const selectedItemType = getSelectedItemType(rowData.itemType);
    const maxQuantity = selectedItemType?.MaxQuantityPerBooking || 100;

    return (
      <InputNumber
        value={rowData.quantity}
        onValueChange={(e) => {
          const newItems = [...watchedItems];
          newItems[rowIndex].quantity = e.value;
          setValue('items', newItems);
        }}
        min={1}
        max={maxQuantity}
        showButtons
        buttonLayout="horizontal"
        step={1}
        className={errors[`quantity_${rowIndex}`] ? 'p-invalid' : ''}
        style={{ width: '100%' }}
      />
    );
  };

  const unitWeightTemplate = (rowData, { rowIndex }) => {
    return (
      <InputNumber
        value={rowData.unitWeight}
        onValueChange={(e) => {
          const newItems = [...watchedItems];
          newItems[rowIndex].unitWeight = e.value;
          setValue('items', newItems);
        }}
        min={0}
        mode="decimal"
        minFractionDigits={2}
        maxFractionDigits={2}
        placeholder="Weight (kg)"
        style={{ width: '100%' }}
      />
    );
  };

  const unitPriceTemplate = (rowData, { rowIndex }) => {
    return (
      <InputNumber
        value={rowData.unitPrice}
        onValueChange={(e) => {
          const newItems = [...watchedItems];
          newItems[rowIndex].unitPrice = e.value;
          setValue('items', newItems);
        }}
        min={0}
        mode="currency"
        currency="USD"
        locale="en-US"
        className={errors[`unitPrice_${rowIndex}`] ? 'p-invalid' : ''}
        style={{ width: '100%' }}
      />
    );
  };

  const treatmentTemplate = (rowData, { rowIndex }) => {
    const selectedTreatment = getSelectedTreatment(rowData.treatment);

    return (
      <Dropdown
        value={selectedTreatment}
        options={treatmentOptions}
        onChange={(e) => handleTreatmentChange(rowIndex, e.value)}
        optionLabel="label"
        placeholder="Select treatment"
        className="w-full"
        clearable
        loading={loadingMasterData}
      />
    );
  };

  const totalTemplate = (rowData, { rowIndex }) => {
    const total = calculateItemTotal(rowIndex);
    return (
      <span className="font-bold" style={{ color: '#3B82F6' }}>
        ${total}
      </span>
    );
  };

  const actionTemplate = (rowData, { rowIndex }) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        onClick={() => remove(rowIndex)}
        disabled={fields.length === 1}
        tooltip="Remove item"
      />
    );
  };

  if (loading && !booking) {
    return (
      <div className="flex justify-content-center align-items-center p-5" style={{ minHeight: '400px' }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (showBill && booking) {
    return (
      <BillPreview 
        booking={booking} 
        billCalculation={billCalculation}
        onPrint={() => window.print()}
        onClose={() => setShowBill(false)}
        onBackToAddItems={() => setShowBill(false)}
      />
    );
  }

  return (
    <div className="add-items-container p-2 p-md-3">
      <Toast ref={toast} position={isMobile ? "top-center" : "top-right"} />
      
      {/* Success Dialog */}
      <Dialog
        header="Success!"
        visible={showSuccessDialog}
        style={{ width: isMobile ? '90vw' : '400px' }}
        modal
        onHide={() => setShowSuccessDialog(false)}
        footer={successDialogFooter}
      >
        <div className="text-center p-3">
          <i className="pi pi-check-circle" style={{ fontSize: '4rem', color: '#4CAF50' }}></i>
          <h3>{addedItemsCount} Item(s) Added Successfully!</h3>
          <p>What would you like to do next?</p>
        </div>
      </Dialog>
      
      {/* Back Button */}
      <div className="mb-3">
        <Button
          label="Back to Search"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={onBackToSearch}
        />
      </div>
      
      {loadingMasterData && (
        <div className="mb-3">
          <Skeleton height="100px" className="mb-2" />
          <Skeleton height="40px" />
        </div>
      )}
      
      {booking && (
        <Card className="mb-4" >
          <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h3 className="m-0">Booking Information</h3>
            <Badge value={booking.status} severity={booking.status === 'Pending' ? 'warning' : 'info'} />
          </div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="mb-2">
                <label className="font-semibold">Reference:</label>
                <span className="ml-2">{booking.bookingreference}</span>
              </div>
              <div className="mb-2">
                <label className="font-semibold">Pickup:</label>
                <span className="ml-2">{new Date(booking.pickupdate).toLocaleString()}</span>
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="mb-2">
                <label className="font-semibold">Delivery:</label>
                <span className="ml-2">{new Date(booking.deliverydate).toLocaleString()}</span>
              </div>
              <div className="mb-2">
                <label className="font-semibold">Status:</label>
                <Badge value={booking.status} className="ml-2" />
              </div>
            </div>
            <div className="col-12">
              <div className="mb-2">
                <label className="font-semibold">Pickup Address:</label>
                <div className="mt-1">{booking.pickupaddress}</div>
              </div>
              <div className="mb-2">
                <label className="font-semibold">Delivery Address:</label>
                <div className="mt-1">{booking.deliveryaddress}</div>
              </div>
            </div>
            {booking.orderitems  && (
              <div className="col-12">
                <div className="mt-2 p-2 bg-green-50 rounded">
                  <i className="pi pi-check-circle text-green-500 mr-2"></i>
                  <label className="font-semibold">Items Already Added:</label>
                  <span className="ml-2">{booking.orderitems.length} item(s) in this order</span>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h3 className="m-0">Add More Items</h3>
            <Button
              label={isMobile ? "Add Item" : "Add Item"}
              icon="pi pi-plus"
              className="p-button-outlined"
              onClick={() => {
                append({
                  itemType: '',
                  itemCategory: '',
                  quantity: 1,
                  unitWeight: 0,
                  unitPrice: 0,
                  treatment: '',
                  instructions: '',
                });
                // Scroll to bottom on mobile when adding new item
                if (isMobile) {
                  setTimeout(() => {
                    const lastItem = document.querySelector('.mobile-item-wrapper:last-child');
                    if (lastItem) {
                      lastItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 100);
                }
              }}
              disabled={loadingMasterData}
            />
          </div>
          
          {fields.length === 0 ? (
            <div className="text-center p-4 text-gray-500">
              <i className="pi pi-inbox" style={{ fontSize: '3rem' }}></i>
              <p>No items added. Click "Add Item" to start.</p>
            </div>
          ) : isMobile ? (
            <div className="mobile-items-container">
              {fields.map((field, index) => (
                <div key={field.id} className="mobile-item-wrapper mb-4">
                  {renderMobileItemCard(watchedItems[index], index)}
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DataTable
                value={fields}
                responsiveLayout="scroll"
                className="p-datatable-sm"
                showGridlines
                scrollable
                scrollHeight="400px"
              >
                <Column field="itemType" header="Item Type*" body={itemTypeTemplate} style={{ minWidth: '200px' }} />
                <Column field="itemCategory" header="Category" body={categoryTemplate} style={{ minWidth: '120px' }} />
                <Column field="quantity" header="Quantity*" body={quantityTemplate} style={{ minWidth: '150px' }} />
                <Column field="unitWeight" header="Unit Weight (kg)" body={unitWeightTemplate} style={{ minWidth: '140px' }} />
                <Column field="unitPrice" header="Unit Price*" body={unitPriceTemplate} style={{ minWidth: '150px' }} />
                <Column field="treatment" header="Treatment" body={treatmentTemplate} style={{ minWidth: '150px' }} />
                <Column header="Total" body={totalTemplate} style={{ minWidth: '100px' }} />
                <Column header="Actions" body={actionTemplate} style={{ minWidth: '80px' }} />
              </DataTable>
            </div>
          )}
          
          <div className="mt-3 p-3  rounded">
            <div className="flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="font-semibold">Items in this batch: {fields.length}</span>
              <span className="text-xl font-bold text-primary">Batch Subtotal: ${calculateTotal()}</span>
            </div>
          </div>
        </Card>

        {billCalculation && (
          <Card className="mt-3" style={{ background: '#c5c27d' }}>
            <h3 className="mt-0">Current Bill Summary (All Items)</h3>
            <div className="grid">
              <div className="col-6">
                <div className="mb-2">Total Subtotal:</div>
                <div className="mb-2">Total Weight:</div>
                <div className="mb-2">Weight Charge:</div>
                <div className="mb-2">Tax (18%):</div>
                <Divider />
                <div className="font-bold">Grand Total:</div>
              </div>
              <div className="col-6 text-right">
                <div className="mb-2">${billCalculation.subtotal?.toFixed(2)}</div>
                <div className="mb-2">{billCalculation.totalWeight?.toFixed(2)} kg</div>
                <div className="mb-2">${billCalculation.weightCharge?.toFixed(2)}</div>
                <div className="mb-2">${billCalculation.taxAmount?.toFixed(2)}</div>
                <Divider />
                <div className="font-bold text-primary text-xl">
                  ${billCalculation.grandTotal?.toFixed(2)}
                </div>
              </div>
            </div>
            <div className="mt-2 text-center text-gray-500">
              <i className="pi pi-info-circle mr-1"></i>
              <small>Bill includes all items added to this order</small>
            </div>
          </Card>
        )}

        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label={isMobile ? "Recalc" : "Recalculate"}
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={() => calculateBill()}
            disabled={loading || loadingMasterData}
          />
          <Button
            label="Add Items to Order"
            icon="pi pi-save"
            type="submit"
            disabled={loading || loadingMasterData || fields.length === 0}
            loading={loading}
          />
        </div>
      </form>

      <style>{`
        @media (max-width: 768px) {
          .add-items-container {
            padding: 0.5rem !important;
          }
          .p-card {
            margin-bottom: 1rem !important;
          }
          .p-button {
            font-size: 0.875rem !important;
          }
          .p-inputtext, .p-inputnumber, .p-dropdown {
            font-size: 0.875rem !important;
          }
          .mobile-item-wrapper {
            margin-bottom: 1rem;
          }
          .mobile-item-wrapper:last-child {
            margin-bottom: 0;
          }
        }
        
        @media print {
          .add-items-container {
            padding: 0 !important;
          }
          .p-button, .p-toolbar, .p-card {
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default AddItemsToBooking;
